import React, { useState, useEffect } from 'react';
import { 
  DollarSign, CheckCircle, Clock, XCircle, RefreshCw, 
  Search, Filter, MessageCircle, Eye, Trash2, ChevronLeft, ChevronRight
} from 'lucide-react';

const SalesManagement = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [searchTerm, setSearchTerm] = useState('');
  const [sending, setSending] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sales`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch sales');

      const data = await response.json();
      setSales(data.sales || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
      alert('Erro ao carregar vendas');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCredentials = async (paymentId) => {
    if (!window.confirm('Deseja reenviar as credenciais via WhatsApp?')) return;

    try {
      setSending(paymentId);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales/${paymentId}/resend-credentials`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to resend');
      }

      alert('✅ Credenciais reenviadas com sucesso!');
      fetchSales();
    } catch (error) {
      console.error('Error resending credentials:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setSending(null);
    }
  };

  const handleProcessPendingPayment = async (paymentId) => {
    if (!window.confirm('⚠️ Processar este pagamento manualmente?\n\nIsso irá:\n✅ Renovar/criar o usuário\n✅ Enviar credenciais via WhatsApp\n✅ Notificar você sobre a venda/renovação')) return;

    try {
      setSending(paymentId);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/process-pending-payment/${paymentId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Falha ao processar');
      }

      if (data.status === 'already_processed') {
        alert('ℹ️ Este pagamento já foi processado anteriormente.');
      } else {
        const tipo = data.is_renewal ? 'Renovação' : 'Nova venda';
        alert(`✅ ${tipo} processada com sucesso!\n\n👤 ${data.customer_name}\n📧 ${data.customer_email}\n\n✅ Credenciais enviadas via WhatsApp`);
      }
      
      fetchSales();
    } catch (error) {
      console.error('Error processing payment:', error);
      alert(`❌ Erro ao processar: ${error.message}`);
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (paymentId, status) => {
    // Apenas permitir excluir pending e rejected
    if (status !== 'pending' && status !== 'rejected') {
      alert('Apenas vendas pendentes ou recusadas podem ser excluídas');
      return;
    }

    if (!window.confirm('Deseja realmente excluir esta venda?')) return;

    try {
      setDeleting(paymentId);
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/sales/${paymentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      alert('✅ Venda excluída com sucesso!');
      fetchSales();
    } catch (error) {
      console.error('Error deleting sale:', error);
      alert('Erro ao excluir venda');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        bg: 'bg-yellow-500/20',
        text: 'text-yellow-500',
        icon: <Clock className="w-4 h-4" />,
        label: 'Pendente'
      },
      approved: {
        bg: 'bg-green-500/20',
        text: 'text-green-500',
        icon: <CheckCircle className="w-4 h-4" />,
        label: 'Aprovado'
      },
      rejected: {
        bg: 'bg-red-500/20',
        text: 'text-red-500',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Rejeitado'
      },
      cancelled: {
        bg: 'bg-gray-500/20',
        text: 'text-gray-500',
        icon: <XCircle className="w-4 h-4" />,
        label: 'Cancelado'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Filtrar e paginar
  const filteredSales = sales
    .filter(sale => {
      const matchesFilter = filter === 'all' || sale.status === filter;
      const matchesSearch = !searchTerm || 
        sale.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.customer_phone?.includes(searchTerm);
      
      return matchesFilter && matchesSearch;
    });

  const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSales = filteredSales.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total: sales.length,
    approved: sales.filter(s => s.status === 'approved').length,
    pending: sales.filter(s => s.status === 'pending').length,
    rejected: sales.filter(s => s.status === 'rejected').length,
    revenue: sales
      .filter(s => s.status === 'approved')
      .reduce((sum, s) => sum + (s.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-500" />
            Gerenciar Vendas
          </h1>
          <p className="text-gray-400 mt-1">
            Visualize e gerencie todas as vendas da plataforma
          </p>
        </div>
        <button
          onClick={fetchSales}
          className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="text-gray-400 text-sm mb-2">Total de Vendas</div>
          <div className="text-3xl font-bold text-white">{stats.total}</div>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="text-gray-400 text-sm mb-2">Aprovadas</div>
          <div className="text-3xl font-bold text-green-500">{stats.approved}</div>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="text-gray-400 text-sm mb-2">Pendentes</div>
          <div className="text-3xl font-bold text-yellow-500">{stats.pending}</div>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="text-gray-400 text-sm mb-2">Rejeitadas</div>
          <div className="text-3xl font-bold text-red-500">{stats.rejected}</div>
        </div>

        <div className="bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-xl p-6 border border-green-500/30">
          <div className="text-gray-300 text-sm mb-2">Receita Total</div>
          <div className="text-3xl font-bold text-green-400">{formatCurrency(stats.revenue)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>

        {/* Status Filter */}
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'Todos' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'approved', label: 'Aprovados' },
            { value: 'rejected', label: 'Rejeitados' }
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setFilter(value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === value
                  ? 'bg-cyan-500 text-white'
                  : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Cliente</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Contato</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Valor</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Plano</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Data</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {paginatedSales.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                    Nenhuma venda encontrada
                  </td>
                </tr>
              ) : (
                paginatedSales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-white">{sale.customer_name}</div>
                        <div className="text-sm text-gray-400">{sale.customer_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-white">{sale.customer_phone}</div>
                        <div className="text-gray-400">{sale.customer_cpf}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-green-400">
                        {formatCurrency(sale.amount)}
                      </div>
                      <div className="text-xs text-gray-500">{sale.payment_method}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {sale.plan_type === 'quarterly' ? (
                          <>
                            <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-semibold">
                              🔥 TRIMESTRAL
                            </span>
                            <span className="text-xs text-gray-400">3 meses</span>
                          </>
                        ) : (
                          <>
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-semibold">
                              MENSAL
                            </span>
                            <span className="text-xs text-gray-400">1 mês</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(sale.status)}
                      {sale.credentials_sent && (
                        <div className="text-xs text-green-400 mt-1 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Credenciais enviadas
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">
                        {formatDate(sale.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {sale.status === 'approved' && sale.user_id && (
                          <button
                            onClick={() => handleResendCredentials(sale.id)}
                            disabled={sending === sale.id}
                            className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Reenviar credenciais"
                          >
                            {sending === sale.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <MessageCircle className="w-4 h-4" />
                            )}
                            <span className="hidden lg:inline">Reenviar</span>
                          </button>
                        )}
                        
                        {/* Botão de processar manualmente - apenas para pending */}
                        {sale.status === 'pending' && (
                          <button
                            onClick={() => handleProcessPendingPayment(sale.id)}
                            disabled={sending === sale.id}
                            className="px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Processar pagamento manualmente"
                          >
                            {sending === sale.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                            <span className="hidden lg:inline">Processar</span>
                          </button>
                        )}
                        
                        {/* Botão de excluir - apenas para pending e rejected */}
                        {(sale.status === 'pending' || sale.status === 'rejected') && (
                          <button
                            onClick={() => handleDelete(sale.id, sale.status)}
                            disabled={deleting === sale.id}
                            className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Excluir venda"
                          >
                            {deleting === sale.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            <span className="hidden lg:inline">Excluir</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, filteredSales.length)} de {filteredSales.length} vendas
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-slate-800 text-gray-400 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-cyan-500 text-white'
                      : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-slate-800 text-gray-400 rounded-lg hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Próxima
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="text-center text-gray-500 text-sm">
        Total: {sales.length} vendas
      </div>
    </div>
  );
};

export default SalesManagement;
